import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import LogActivityTable from './LogActivityTable';
import SignatureFlowTable from './SignatureFlowTable';
import apiService from '../services/api';
import {
  SignatureAttachment,
  SignatureFlow,
  SignatureLookupResponse,
  UserLogResponse
} from '../types';

type LanguageCode = 'en' | 'he';

interface SignatureMetrics {
  htmlBytes: number;
  mediaBytes: number;
  attachmentBytes: number;
  totalBytes: number;
  attachmentCount: number;
  mediaCount: number;
}

interface SignatureLookupResult {
  response: SignatureLookupResponse;
  renderedSignature: string;
  requestMs: number;
  metrics: SignatureMetrics;
}

interface ErrorState {
  message: string;
  status?: number;
  smtpRequestId?: string | null;
  flow?: SignatureFlow | null;
}

interface LogLoadState {
  loading: boolean;
  error: string | null;
  data: UserLogResponse | null;
}

const LANGUAGE_OPTIONS: Array<{ value: LanguageCode; label: string }> = [
  { value: 'he', label: 'Hebrew' },
  { value: 'en', label: 'English' }
];

const EMPTY_LOG_STATE: LogLoadState = {
  loading: false,
  error: null,
  data: null
};

function getByteLength(value: string) {
  return new TextEncoder().encode(value).length;
}

function getBase64ByteLength(value?: string) {
  if (!value) {
    return 0;
  }

  const normalized = value.replace(/\s+/g, '');
  const padding = normalized.endsWith('==') ? 2 : normalized.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((normalized.length * 3) / 4) - padding);
}

function getAttachmentSize(attachment: SignatureAttachment) {
  if (typeof attachment.size === 'number' && Number.isFinite(attachment.size)) {
    return attachment.size;
  }

  return getBase64ByteLength(attachment.contentB64);
}

function isMediaAttachment(attachment: SignatureAttachment) {
  const contentType = String(attachment.contentType || '').toLowerCase();
  return (
    contentType.startsWith('image/') ||
    contentType.startsWith('audio/') ||
    contentType.startsWith('video/')
  );
}

function normalizeToken(value: string) {
  return String(value || '').trim().replace(/[<>]/g, '');
}

function inlineCidAttachments(signature: string, attachments: SignatureAttachment[]) {
  if (!signature || attachments.length === 0) {
    return signature;
  }

  const byToken = new Map<string, SignatureAttachment>();

  attachments.forEach((attachment) => {
    const cid = normalizeToken(attachment.cid || '');
    const filename = normalizeToken(attachment.filename || '');

    if (cid) {
      byToken.set(cid, attachment);
    }
    if (filename) {
      byToken.set(filename, attachment);
    }
  });

  return signature.replace(/src\s*=\s*(["'])cid:([^"']+)\1/gi, (_match, quote, token) => {
    const attachment = byToken.get(normalizeToken(token));
    if (!attachment?.contentB64) {
      return _match;
    }

    const contentType = attachment.contentType || 'application/octet-stream';
    return `src=${quote}data:${contentType};base64,${attachment.contentB64}${quote}`;
  });
}

function buildPreviewDocument(signatureHtml: string) {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <base target="_blank" />
    <style>
      body {
        margin: 0;
        padding: 16px;
        background: #ffffff;
        color: #1f2933;
        font-family: Arial, Helvetica, sans-serif;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      table {
        max-width: 100%;
      }
      a {
        word-break: break-word;
      }
    </style>
  </head>
  <body>
    ${signatureHtml || '<div style="color:#6b7280;">No signature returned.</div>'}
  </body>
</html>`;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(requestMs: number) {
  if (requestMs < 1000) {
    return `${requestMs.toFixed(0)} ms`;
  }

  return `${(requestMs / 1000).toFixed(2)} s`;
}

function getErrorState(error: any): ErrorState {
  const responseData = error?.response?.data || {};
  const details = responseData.details || null;

  return {
    message:
      details ||
      responseData.message ||
      responseData.error ||
      error?.message ||
      'Failed to load signature',
    status: error?.response?.status,
    smtpRequestId: responseData.smtpRequestId || responseData.flow?.smtpRequestId || null,
    flow: responseData.flow || null
  };
}

function buildMetrics(signature: string, attachments: SignatureAttachment[]): SignatureMetrics {
  const htmlBytes = getByteLength(signature || '');
  const attachmentBytes = attachments.reduce((sum, attachment) => sum + getAttachmentSize(attachment), 0);
  const mediaAttachments = attachments.filter(isMediaAttachment);
  const mediaBytes = mediaAttachments.reduce((sum, attachment) => sum + getAttachmentSize(attachment), 0);

  return {
    htmlBytes,
    mediaBytes,
    attachmentBytes,
    totalBytes: htmlBytes + attachmentBytes,
    attachmentCount: attachments.length,
    mediaCount: mediaAttachments.length
  };
}

const SignatureLookupTool: React.FC = () => {
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState<LanguageCode>('he');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SignatureLookupResult | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);
  const [lastRequestMs, setLastRequestMs] = useState<number | null>(null);
  const [activeLogEmail, setActiveLogEmail] = useState('');
  const [serverLogState, setServerLogState] = useState<LogLoadState>(EMPTY_LOG_STATE);
  const [smtpLogState, setSmtpLogState] = useState<LogLoadState>(EMPTY_LOG_STATE);
  const lookupRequestRef = useRef(0);

  const previewDocument = useMemo(
    () => buildPreviewDocument(result?.renderedSignature || ''),
    [result]
  );
  const activeFlow = result?.response.flow || error?.flow || null;

  const loadLogsForSource = async (
    targetEmail: string,
    source: 'server' | 'smtp',
    lookupId: number
  ) => {
    const setState = source === 'server' ? setServerLogState : setSmtpLogState;

    try {
      const response = await apiService.getUserLogs(targetEmail, source, { limit: 250 });
      if (lookupRequestRef.current !== lookupId) {
        return;
      }

      setState({
        loading: false,
        error: null,
        data: response
      });
    } catch (requestError: any) {
      if (lookupRequestRef.current !== lookupId) {
        return;
      }

      setState({
        loading: false,
        error: getErrorState(requestError).message,
        data: null
      });
    }
  };

  const handleLookup = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError({ message: 'Email is required.' });
      setResult(null);
      return;
    }

    const lookupId = lookupRequestRef.current + 1;
    lookupRequestRef.current = lookupId;

    setLoading(true);
    setError(null);
    setResult(null);
    setActiveLogEmail(trimmedEmail);
    setServerLogState({ loading: true, error: null, data: null });
    setSmtpLogState({ loading: true, error: null, data: null });

    const startedAt = performance.now();

    void loadLogsForSource(trimmedEmail, 'server', lookupId);
    void loadLogsForSource(trimmedEmail, 'smtp', lookupId);

    try {
      const response = await apiService.getSignaturePreview(trimmedEmail, language, {
        skipCache: true
      });
      const requestMs = performance.now() - startedAt;
      const attachments = Array.isArray(response.attachments) ? response.attachments : [];
      const renderedSignature = inlineCidAttachments(response.signature || '', attachments);

      setResult({
        response,
        renderedSignature,
        requestMs,
        metrics: buildMetrics(response.signature || '', attachments)
      });
      setLastRequestMs(requestMs);
    } catch (requestError: any) {
      setError(getErrorState(requestError));
      setLastRequestMs(performance.now() - startedAt);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    lookupRequestRef.current += 1;
    setEmail('');
    setLanguage('he');
    setLoading(false);
    setResult(null);
    setError(null);
    setLastRequestMs(null);
    setActiveLogEmail('');
    setServerLogState(EMPTY_LOG_STATE);
    setSmtpLogState(EMPTY_LOG_STATE);
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4">Signature Lookup</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
              Run a live signature fetch for any mailbox, inspect the rendered output, and measure the HTML payload, embedded media payload, and total response size.
            </Typography>
          </Box>
          <Chip label="Live fetch" color="primary" variant="outlined" />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 2fr) minmax(180px, 1fr) auto auto' },
            gap: 2,
            mt: 3,
            alignItems: 'start'
          }}
        >
          <TextField
            label="User email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="user@company.com"
            fullWidth
            disabled={loading}
          />
          <FormControl fullWidth disabled={loading}>
            <InputLabel id="signature-language-label">Language</InputLabel>
            <Select
              labelId="signature-language-label"
              label="Language"
              value={language}
              onChange={(event) => setLanguage(event.target.value as LanguageCode)}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleLookup}
            disabled={loading}
            sx={{ minWidth: 150, height: 56 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Load signature'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={loading && !result && !error}
            sx={{ minWidth: 120, height: 56 }}
          >
            Reset
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          This tool bypasses the server cache so the timing and media-size numbers reflect a fresh signature load.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <Box>
              <Typography variant="subtitle2">Signature load failed</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {error.message}
              </Typography>
              {error.smtpRequestId && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  SMTP request ID: {error.smtpRequestId}
                </Typography>
              )}
              {typeof error.status === 'number' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  HTTP status: {error.status}
                </Typography>
              )}
            </Box>
          </Alert>
        )}

        {(result || lastRequestMs !== null) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
              gap: 2,
              mt: 3
            }}
          >
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Request time</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {lastRequestMs !== null ? formatDuration(lastRequestMs) : 'N/A'}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Signature HTML</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {result ? formatBytes(result.metrics.htmlBytes) : 'N/A'}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Embedded images/media</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {result ? formatBytes(result.metrics.mediaBytes) : 'N/A'}
              </Typography>
              {result && (
                <Typography variant="caption" color="text.secondary">
                  {result.metrics.mediaCount} item{result.metrics.mediaCount === 1 ? '' : 's'}
                </Typography>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Total payload</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {result ? formatBytes(result.metrics.totalBytes) : 'N/A'}
              </Typography>
              {result && result.metrics.attachmentBytes !== result.metrics.mediaBytes && (
                <Typography variant="caption" color="text.secondary">
                  Includes {formatBytes(result.metrics.attachmentBytes - result.metrics.mediaBytes)} of non-media attachments
                </Typography>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">Response details</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {result ? `${result.metrics.attachmentCount} attachments` : 'N/A'}
              </Typography>
              {result && (
                <Typography variant="caption" color="text.secondary">
                  {result.response.cachedVersion ? 'Returned from cache' : 'Fresh backend result'}
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        {activeFlow && (
          <Box sx={{ mt: 3 }}>
            <SignatureFlowTable flow={activeFlow} />
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.7fr) minmax(320px, 1fr)' },
            gap: 2,
            mt: 3
          }}
        >
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Rendered signature</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              The preview resolves inline CID images from the returned attachment payload so you can inspect the signature as it is meant to appear.
            </Typography>
            {result ? (
              <Box
                component="iframe"
                title="Signature preview"
                sandbox=""
                srcDoc={previewDocument}
                sx={{
                  width: '100%',
                  minHeight: 420,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: '#fff'
                }}
              />
            ) : (
              <Box
                sx={{
                  minHeight: 220,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  px: 3,
                  textAlign: 'center'
                }}
              >
                Enter an email and language, then load a signature to preview it here.
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Attachment breakdown</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sizes are calculated from the attachment payload returned by the signature endpoint.
            </Typography>

            {result && result.response.attachments && result.response.attachments.length > 0 ? (
              <Stack spacing={1.25} sx={{ mt: 2 }}>
                {result.response.attachments.map((attachment, index) => (
                  <Box
                    key={`${attachment.cid || attachment.filename || 'attachment'}-${index}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1.25
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                      {attachment.filename || attachment.cid || `Attachment ${index + 1}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {attachment.contentType || 'Unknown content type'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {formatBytes(getAttachmentSize(attachment))}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  mt: 2,
                  minHeight: 180,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 3,
                  color: 'text.secondary'
                }}
              >
                {result ? 'No attachments were returned for this signature.' : 'Attachment details will appear here after a lookup.'}
              </Box>
            )}
          </Paper>
        </Box>

        {activeLogEmail && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5">Related log activity</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>
              Matching rows for {activeLogEmail} are loaded in the background from the server and SMTP log files. Each table includes derived action labels, searchable structured columns, pagination, and the original raw log line.
            </Typography>

            <Box sx={{ display: 'grid', gap: 2 }}>
              <LogActivityTable
                title="Server logs"
                loading={serverLogState.loading}
                error={serverLogState.error}
                data={serverLogState.data}
              />
              <LogActivityTable
                title="SMTP logs"
                loading={smtpLogState.loading}
                error={smtpLogState.error}
                data={smtpLogState.data}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SignatureLookupTool;
